import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Info } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import SEOHead from '@/components/marketing/SEOHead';
import PageHeader from '@/components/marketing/PageHeader';
import CTASection from '@/components/marketing/CTASection';
import DecorBackground from '@/components/marketing/DecorBackground';
import { Button } from '@/components/ui/button';
import { CASE_STUDIES, CASE_STUDIES_INDEX } from '@/data/case-studies-content';
import { breadcrumbJsonLd } from '@/lib/jsonld';

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

export default function CaseStudiesIndex() {
  const reduced = useReducedMotion();
  const stg = reduced ? {} : stagger;
  const up = reduced ? {} : fadeUp;
  const initial = reduced ? false : 'hidden';

  const jsonLd = breadcrumbJsonLd([
    { label: 'Home', path: '/' },
    { label: 'Case Studies' },
  ]);

  return (
    <>
      <SEOHead path="/case-studies" jsonLd={jsonLd} />
      <PageHeader
        breadcrumbs={[{ label: 'Case Studies' }]}
        eyebrow={CASE_STUDIES_INDEX.eyebrow}
        title={CASE_STUDIES_INDEX.title}
        subtitle={CASE_STUDIES_INDEX.intro}
      >
        <div className="mt-2 flex flex-wrap gap-3">
          <Button
            asChild
            data-testid="case-studies-apply-cta"
            className="btn-primary-gradient text-white rounded-xl px-6 py-5 text-[14px] font-semibold border-0"
          >
            <Link to="/apply" className="inline-flex items-center gap-2">
              Apply as Advertiser <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            className="bg-white/[0.04] text-white border-white/15 hover:bg-white/10 hover:text-white rounded-xl px-5 py-5 text-[14px] font-semibold transition-colors backdrop-blur-md"
          >
            <Link to="/lead-generation" className="inline-flex items-center gap-2">
              Explore lead-gen verticals
            </Link>
          </Button>
        </div>
      </PageHeader>

      {/* Disclaimer band */}
      <section
        className="relative bg-[#06101F] text-white isolate overflow-hidden border-y border-white/5"
        data-testid="case-studies-disclaimer"
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-start gap-3 text-[12.5px] text-slate-300/90 leading-relaxed">
            <Info className="h-4 w-4 mt-0.5 text-[#7DDCFF] shrink-0" />
            <p>{CASE_STUDIES_INDEX.disclaimer}</p>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section
        className="relative bg-[#050B17] text-white isolate overflow-hidden"
        data-testid="case-studies-grid-section"
      >
        <DecorBackground variant="section" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <motion.div
            variants={stg}
            initial={initial}
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="case-studies-cards-grid"
          >
            {CASE_STUDIES.map((c) => {
              const Icon = c.icon;
              return (
                <motion.div key={c.slug} variants={up}>
                  <Link
                    to={`/case-studies/${c.slug}`}
                    data-testid={`case-study-card-${c.slug}`}
                    className="
                      group relative block h-full rounded-2xl overflow-hidden
                      bg-gradient-to-b from-white/[0.05] to-white/[0.02]
                      border border-white/10
                      backdrop-blur-xl
                      transition-colors hover:border-[#7DDCFF]/35
                      shadow-[0_24px_60px_rgba(0,0,0,0.45)]
                    "
                  >
                    <div className="relative h-[180px] overflow-hidden">
                      <img
                        src={c.heroImage}
                        alt={c.imageCaption}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover scale-[1.02] group-hover:scale-[1.06] transition-transform duration-700"
                      />
                      <div
                        aria-hidden="true"
                        className="absolute inset-0"
                        style={{
                          background:
                            'linear-gradient(180deg, rgba(5,11,23,0.35) 0%, rgba(5,11,23,0.85) 65%, rgba(5,11,23,1) 100%)',
                        }}
                      />
                      <div
                        aria-hidden="true"
                        className="absolute inset-0 mix-blend-screen opacity-40"
                        style={{
                          background: `radial-gradient(circle at 80% 20%, ${c.accent}26, transparent 60%)`,
                        }}
                      />
                      <span
                        className="absolute top-4 left-4 inline-flex h-10 w-10 items-center justify-center rounded-xl border backdrop-blur-md"
                        style={{
                          background: `linear-gradient(180deg, ${c.accent}33, ${c.accent}10)`,
                          borderColor: `${c.accent}66`,
                          color: c.accent,
                          boxShadow: `0 0 22px ${c.accent}55`,
                        }}
                      >
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="absolute bottom-4 left-4 right-4 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-300/95">
                        {c.eyebrow}
                      </span>
                    </div>
                    <div className="p-6">
                      <h2 className="font-display text-[19px] font-bold tracking-[-0.01em] text-white group-hover:text-[#7DDCFF] transition-colors leading-snug">
                        {c.title}
                      </h2>
                      <p className="mt-2.5 text-[13px] text-slate-300/90 leading-[1.65] line-clamp-3">
                        {c.short}
                      </p>
                      <div className="mt-4 flex flex-wrap gap-1.5">
                        {c.geos.slice(0, 3).map((g) => (
                          <span
                            key={g}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-medium text-slate-300 bg-white/[0.04] border border-white/10"
                          >
                            {g}
                          </span>
                        ))}
                      </div>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#22D3EE]">
                        Read case study <ArrowUpRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <CTASection
        eyebrow="Have a funnel ready?"
        title="Let’s get you some business"
        subtitle="Apply as advertiser and your campaign reviewer will recommend a format mix, GEO plan, and source-level rules — matched to a vertical we already know how to run."
        primaryTo="/apply"
      />
    </>
  );
}
